// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedTime from "@insite/client-framework/Common/Utilities/getLocalizedTime";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { BillToModel, CarrierDto, ShipToModel, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import ShippingAddressInfoDisplay, {
    ShippingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckoutMoveToStep from "@pace/App/Components/Checkout/CheckoutMoveToStep";
import SectionTitle from "@pace/App/Components/Common/SectionTitle";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { CHECKOUT_STEPS, CHECKOUT_WHEN_TO_DELIVER } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { parseBoolean } from "@pace/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    billTo: BillToModel;
    shipTo: ShipToModel;
    carrier: CarrierDto;
    shipVia: ShipViaDto;
    deliveryDate: Date | null;
    onEditBillTo: () => void;
    onEditShipTo: () => void;
    extendedStyles?: BillingAndShippingAddressStyles;
    // XNG-Changes
    deliverAnytime?: boolean;
}

export interface BillingAndShippingAddressStyles {
    container?: GridContainerProps;
    carrierGridItem?: GridItemProps;
    carrierHeadingText?: TypographyPresentationProps;
    carrierText?: TypographyPresentationProps;
    shippingServiceGridItem?: GridItemProps;
    shippingServiceHeadingText?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateHeadingText?: TypographyPresentationProps;
    deliveryDateText?: TypographyPresentationProps;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    // XNG-Changes
    section?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    label?: TypographyPresentationProps;
    hr?: InjectableCss;
}

export const billingAndShippingAddressStyles: BillingAndShippingAddressStyles = {
    container: { gap: 20 },
    carrierGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    shippingServiceHeadingText: { weight: 600 },
    shippingServiceGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    carrierHeadingText: { weight: 600 },
    deliveryDateGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryDateHeadingText: { weight: 600 },
    shippingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    billingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
};

// XNG-Changes: Structure
const BillingAndShippingInfo = ({
    billTo,
    shipTo,
    carrier,
    shipVia,
    deliveryDate,
    onEditBillTo,
    onEditShipTo,
    extendedStyles,
    // XNG-Changes
    deliverAnytime,
}: OwnProps) => {
    const styles = mergeToNew(billingAndShippingAddressStyles, extendedStyles);
    const { notes, shipPartial } = useAppSelector(state => {
        const { cartId } = state.pages.checkoutShipping;
        const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
        return {
            notes: cart?.notes,
            shipPartial: (state as CustomAppState).xCheckout.shipping.shipPartial,
        };
    });

    return (
        <>
            <SectionTitle
                title={translate("Addresses")}
                extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.SHIPPING} />}
                border="none"
            />
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.section}>
                    <ShippingAddressInfoDisplay
                        shipTo={shipTo}
                        onEdit={onEditShipTo}
                        extendedStyles={styles.shippingAddress}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.section}>
                    <BillingAddressInfoDisplay
                        billTo={billTo}
                        onEdit={onEditBillTo}
                        extendedStyles={styles.billingAddress}
                    />
                </StyledWrapper>
                {notes && (
                    <StyledWrapper {...styles.section} {...{ fullWidth: true }}>
                        <Typography variant="h6">{translate("Special Shipping / Handling Instructions")}</Typography>
                        <Typography {...styles.value}>{notes}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
            <SectionTitle
                title={translate("Shipping")}
                extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.SHIPPING} />}
                border="none"
            />
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.section}>
                    <Typography {...styles.sectionTitle}>{translate("Preference")}</Typography>
                    <Typography {...styles.value}>
                        {parseBoolean(shipPartial)
                            ? CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAvailable
                            : CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAllAvailable}
                    </Typography>
                </StyledWrapper>
                {deliveryDate && (
                    <StyledWrapper {...styles.section}>
                        <Typography {...styles.sectionTitle}>{translate("Requested Delivery Date")}</Typography>
                        <Typography {...styles.value} data-test-selector="requestedDeliveryDateValue">
                            <Typography {...styles.label}>{translate("Date")}:</Typography>
                            <LocalizedDateTime
                                dateTime={deliveryDate}
                                // XNG-Changes
                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                            />
                        </Typography>
                        <Typography {...styles.value} data-test-selector="requestedDeliveryDateTimeValue">
                            <Typography {...styles.label}>{translate("Time")}:</Typography>
                            {deliverAnytime
                                ? translate("Anytime")
                                : getLocalizedTime({
                                      dateTime: deliveryDate,
                                      options: {
                                          hour: "numeric",
                                          minute: "numeric",
                                      },
                                  })}
                        </Typography>
                    </StyledWrapper>
                )}
                {/** XNG-Changes: PAC-530 */}
                {/* {carrier?.description && (
                    <StyledWrapper {...styles.section}>
                        <Typography {...styles.sectionTitle}>{translate("Carrier")}</Typography>
                        <Typography {...styles.value}>{carrier?.description}</Typography>
                    </StyledWrapper>
                )}
                {shipVia?.description && (
                    <StyledWrapper {...styles.section}>
                        <Typography {...styles.sectionTitle}>{translate("Service")}</Typography>
                        <Typography {...styles.value}>{shipVia?.description}</Typography>
                    </StyledWrapper>
                )} */}
            </StyledWrapper>
        </>
    );
};

export default BillingAndShippingInfo;
